<template>
  <div class="pzwh-chat-container" v-if="showDialog" @paste="handlePaste">
    <div class="chat-header">
      <h3>{{ isPwkMode ? '通过文件或文本添加批文到批文库' : '保健食品批文识别' }}</h3>
      <button @click="closeDialog" class="close-btn">×</button>
    </div>

    <div class="chat-messages" ref="messagesContainer">
      <div
        v-for="(message, index) in messages"
        :key="index"
        :class="['message', message.type]"
      >
        <div class="message-content">
          <div v-if="message.type === 'user'" class="user-label">管理员</div>
          <div v-else class="ai-label">系统</div>
          <div class="message-text">{{ message.text }}</div>
          <div v-if="message.files && message.files.length > 0" class="message-files">
            <div v-for="(file, fileIndex) in message.files" :key="fileIndex" class="file-item">
              <span class="file-icon">{{ getFileIcon(file.type) }}</span>
              <span class="file-name">{{ file.name }}</span>
            </div>
          </div>
          <div v-if="message.result" class="message-result">
            <div class="result-summary">
              <strong>识别结果：</strong>
              <span v-if="message.result.success">成功</span>
              <span v-else class="error">失败</span>
            </div>
            <!-- 批文库添加模式结果 -->
            <template v-if="isPwkMode">
              <div v-if="message.result.addedCount > 0" class="saved-count">
                <strong>已加入批文库：</strong>{{ message.result.addedCount }} 条
              </div>
              <div v-if="message.result.addedList && message.result.addedList.length > 0" class="pzwh-list">
                <strong>新增批文：</strong>
                <ul>
                  <li v-for="(item, pzwhIndex) in message.result.addedList" :key="'pwk-added-'+pzwhIndex">
                    {{ item.cpmc }}（{{ item.pzwh }} / {{ item.sqrzwmc || '-' }}）
                  </li>
                </ul>
              </div>
              <div v-if="message.result.duplicateCount > 0" class="updated-count">
                <strong>库中已存在（同批文号+同申请人，未重复添加）：</strong>{{ message.result.duplicateCount }} 条
              </div>
              <div v-if="message.result.duplicateList && message.result.duplicateList.length > 0" class="pzwh-list">
                <ul>
                  <li v-for="(item, pzwhIndex) in message.result.duplicateList" :key="'pwk-dup-'+pzwhIndex">
                    {{ item.cpmc }}（{{ item.pzwh }} / {{ item.sqrzwmc || '-' }}）
                  </li>
                </ul>
              </div>
              <div v-if="message.result.notFoundCount > 0" class="not-found-count">
                <strong>未在数据库中匹配到：</strong>
                <ul>
                  <li v-for="(pzwh, pzwhIndex) in message.result.notFoundList" :key="'pwk-nf-'+pzwhIndex">
                    {{ pzwh }}
                  </li>
                </ul>
              </div>
            </template>
            <!-- 管理员识别入库模式结果 -->
            <template v-else>
              <div v-if="message.result.savedCount > 0" class="saved-count">
                <strong>新增入库：</strong>{{ message.result.savedCount }} 条
              </div>
              <div v-if="message.result.savedPzwhList && message.result.savedPzwhList.length > 0" class="pzwh-list">
                <strong>新增批文号：</strong>
                <ul>
                  <li v-for="(pzwh, pzwhIndex) in message.result.savedPzwhList" :key="'saved-'+pzwhIndex">
                    {{ pzwh }}
                  </li>
                </ul>
              </div>
              <div v-if="message.result.updatedCount > 0" class="updated-count">
                <strong>更新入库：</strong>{{ message.result.updatedCount }} 条
              </div>
              <div v-if="message.result.updatedPzwhList && message.result.updatedPzwhList.length > 0" class="pzwh-list">
                <strong>更新批文号：</strong>
                <ul>
                  <li v-for="(pzwh, pzwhIndex) in message.result.updatedPzwhList" :key="'updated-'+pzwhIndex">
                    {{ pzwh }}
                  </li>
                </ul>
              </div>
              <div v-if="message.result.pzwhList && message.result.pzwhList.length > 0 && !message.result.savedPzwhList" class="pzwh-list">
                <strong>识别到的批文号：</strong>
                <ul>
                  <li v-for="(pzwh, pzwhIndex) in message.result.pzwhList" :key="pzwhIndex">
                    {{ pzwh }}
                  </li>
                </ul>
              </div>
              <div v-if="message.result.priceList && message.result.priceList.length > 0" class="price-list">
                <strong>识别到的价格：</strong>
                <ul>
                  <li v-for="(price, priceIndex) in message.result.priceList" :key="priceIndex">
                    {{ price }}
                  </li>
                </ul>
              </div>
              <div v-if="message.result.deletedCount !== undefined && message.result.deletedCount > 0" class="deleted-count">
                <strong>成功删除：</strong>{{ message.result.deletedCount }} 条
              </div>
            </template>
            <div v-if="message.result.errorMessage" class="error-message">
              <strong>提示信息：</strong>{{ message.result.errorMessage }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="message ai">
        <div class="message-content">
          <div class="ai-label">系统</div>
          <div class="message-text">
            <span class="loading-dots">正在识别中</span>
          </div>
        </div>
      </div>
    </div>

    <div class="chat-input-area">
      <div class="file-upload-buttons">
        <label class="upload-btn">
          <span>� 选择文件</span>
          <input
            type="file"
            accept=".pdf,.xlsx,.xls,image/*,.txt,.doc,.docx"
            multiple
            @change="handleFileUpload"
            style="display: none"
          />
        </label>
        <span class="upload-tip">支持：图片、PDF、Excel、文本文件（可多选），可直接粘贴文件/图片</span>
      </div>

      <div v-if="selectedFiles.length > 0" class="selected-files">
        <div
          v-for="(file, index) in selectedFiles"
          :key="index"
          class="selected-file-item"
        >
          <span class="file-icon">{{ getFileIcon(file.type) }}</span>
          <span class="file-name">{{ file.name }}</span>
          <span class="file-size">({{ formatFileSize(file.file.size) }})</span>
          <button @click="removeFile(index)" class="remove-file">×</button>
        </div>
      </div>

      <div class="input-area">
        <textarea
          v-model="inputText"
          placeholder="输入文字或上传文件进行批文识别...&#10;可直接粘贴文件/图片（截图后 Ctrl+V）&#10;提示：输入删除+批准文号可删除对应数据&#10;例如：删除 国食健注G20240001"
          @keydown.ctrl.enter="sendMessage"
          rows="4"
        ></textarea>
        <button @click="sendMessage" class="send-btn" :disabled="isLoading">
          {{ isLoading ? '识别中...' : '发送' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import api from '@/api/request'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import config from '@/config'
import { isUnauthorized, requireLogin } from '@/utils/loginGuard'

const route = useRoute()
const router = useRouter()

const emit = defineEmits(['close'])
const userStore = useUserStore()

// mode: admin-管理员批文识别（默认），pwk-批文库文件添加
const props = defineProps({
  mode: { type: String, default: 'admin' },
  pwkId: { type: [Number, String], default: null }
})

const isPwkMode = computed(() => props.mode === 'pwk')
const chatScene = computed(() => isPwkMode.value ? 'pwk' : 'admin')

const showDialog = ref(false)
const inputText = ref('')
const messages = ref([])
const selectedFiles = ref([])
const isLoading = ref(false)
const messagesContainer = ref(null)

// 存储配置
const STORAGE_KEY_PREFIX = config.storageKeys.chatHistory
const STORAGE_EXPIRY_DAYS = config.systemConfig.chat.expireDays

// 已登录请求头
const authHeaders = () => {
  const token = localStorage.getItem('token')
  return token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {}
}

// 检查是否为管理员（仅 admin 模式需要）
const checkAdminPermission = () => {
  const userInfo = userStore.userInfo
  if (!userInfo) {
    return false
  }

  // 根据 role 字段判断
  if (userInfo.role === 'admin') {
    return true
  }

  // 根据用户名判断（兼容旧数据）
  if (userInfo.username) {
    const username = userInfo.username.toLowerCase()
    return username === 'admin' ||
           username === 'administrator' ||
           username.startsWith('admin_')
  }

  return true
}

// 获取用户存储键（优先手机号，全局唯一身份标识）
const getUserStorageKey = () => {
  const userInfo = userStore.userInfo
  if (userInfo?.phone) {
    return STORAGE_KEY_PREFIX + 'phone_' + userInfo.phone
  }
  if (userInfo?.username) {
    return STORAGE_KEY_PREFIX + userInfo.username.toLowerCase()
  }
  return STORAGE_KEY_PREFIX + 'guest'
}

// 加载历史记录期间不写库，避免把历史消息重复追加
let loadingHistory = false

// 保存聊天记录：优先写入数据库（按手机号+场景），失败时兜底存本地
const saveMessagesToStorage = () => {
  if (loadingHistory) return
  if (messages.value.length === 0) return

  const latest = messages.value[messages.value.length - 1]
  try {
    api.post('/chat/history', {
      scene: chatScene.value,
      role: latest.type === 'user' ? 'user' : 'ai',
      content: latest.text || '',
      fileNames: latest.files && latest.files.length > 0 ? JSON.stringify(latest.files.map(f => f.name)) : null,
      resultJson: latest.result ? JSON.stringify(latest.result) : null
    }, { headers: authHeaders() }).catch(() => {
      // 数据库保存失败时兜底存本地
      saveToLocalStorage()
    })
  } catch (error) {
    console.error('保存聊天记录失败:', error)
  }
  // 同时写入本地（离线兜底，双写）
  saveToLocalStorage()
}

// 保存聊天记录到本地存储（离线兜底）
const saveToLocalStorage = () => {
  const storageKey = getUserStorageKey()
  const data = {
    messages: messages.value,
    timestamp: Date.now(),
    expiry: Date.now() + (STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000) // 30天过期
  }
  try {
    localStorage.setItem(storageKey, JSON.stringify(data))
  } catch (error) {
    console.error('保存聊天记录失败:', error)
  }
}

// 从数据库加载聊天记录（失败时从本地加载）
const loadMessagesFromStorage = () => {
  loadingHistory = true
  api.get('/chat/history', {
    params: { scene: chatScene.value },
    headers: authHeaders()
  }).then(resp => {
    const list = resp.data || []
    if (list.length > 0) {
      // 数据库记录转为前端消息格式
      messages.value = list.map(m => {
        let result = null
        try {
          result = m.resultJson ? JSON.parse(m.resultJson) : null
        } catch (e) {
          result = null
        }
        return {
          type: m.role === 'user' ? 'user' : 'ai',
          text: m.content || '',
          files: m.fileNames ? JSON.parse(m.fileNames).map(n => ({ name: n, type: '' })) : [],
          result: result
        }
      })
    }
    loadingHistory = false
    loadFromLocalStorage()
  }).catch(() => {
    loadingHistory = false
    loadFromLocalStorage()
  })
}

// 从本地存储加载（离线兜底）
const loadFromLocalStorage = () => {
  const storageKey = getUserStorageKey()
  try {
    const storedData = localStorage.getItem(storageKey)
    if (storedData) {
      const data = JSON.parse(storedData)
      // 检查是否过期
      if (data.expiry && data.expiry > Date.now()) {
        if (messages.value.length === 0) {
          messages.value = data.messages || []
        }
      } else {
        // 过期，清除存储
        localStorage.removeItem(storageKey)
      }
    }
  } catch (error) {
    console.error('加载聊天记录失败:', error)
  }
}

// 清理过期的聊天记录
const cleanupExpiredMessages = () => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        const storedData = localStorage.getItem(key)
        if (storedData) {
          try {
            const data = JSON.parse(storedData)
            if (data.expiry && data.expiry <= Date.now()) {
              localStorage.removeItem(key)
            }
          } catch (e) {
            // 解析错误，清除无效数据
            localStorage.removeItem(key)
          }
        }
      }
    }
  } catch (error) {
    console.error('清理过期聊天记录失败:', error)
  }
}

// 打开对话框
const openDialog = () => {
  if (!isPwkMode.value && !checkAdminPermission()) {
    alert('只有管理员才能使用此功能')
    return
  }
  showDialog.value = true
  // 加载聊天记录
  loadMessagesFromStorage()
  // 初始化欢迎消息
  if (messages.value.length === 0) {
    if (isPwkMode.value) {
      messages.value.push({
        type: 'ai',
        text: '欢迎使用批文库文件添加功能！\n\n您可以输入文字或上传图片、PDF、Excel文件，系统会自动识别其中的保健食品批文号，并加入当前批文库。\n同一批文库内，批准文号+申请人中文名称相同的批文不会重复添加。'
      })
    } else {
      messages.value.push({
        type: 'ai',
        text: '欢迎使用保健食品批文识别系统！\n\n您可以：\n1. 输入文字、上传图片、PDF或Excel文件，系统会自动识别其中的保健食品批文号并入库\n2. 批文号后跟随数字或数字+万视为价格，会一并保存\n3. 输入"删除 批准文号"可删除对应数据\n\n支持批量上传多个文件，系统会根据文件类型自动识别处理。'
      })
    }
  }
}

// 关闭对话框
const closeDialog = () => {
  // 保存聊天记录
  saveMessagesToStorage()
  showDialog.value = false
  emit('close')
}

// 滚动到底部
const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

// 获取文件图标
const getFileIcon = (type) => {
  switch (type) {
    case 'image':
      return '📷'
    case 'pdf':
      return '📄'
    case 'excel':
      return '📊'
    case 'text':
      return '📝'
    case 'word':
      return '📘'
    default:
      return '📁'
  }
}

// 格式化文件大小
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 获取文件类型
const getFileType = (file) => {
  const fileName = (file.name || '').toLowerCase()
  if (file.type.startsWith('image/')) {
    return 'image'
  } else if (fileName.endsWith('.pdf')) {
    return 'pdf'
  } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return 'excel'
  } else if (fileName.endsWith('.txt') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
    return 'text'
  }
  return 'other'
}

// 统一处理文件上传（支持多文件）
const handleFileUpload = (event) => {
  const files = Array.from(event.target.files)
  files.forEach(file => {
    selectedFiles.value.push({
      file: file,
      name: file.name,
      type: getFileType(file)
    })
  })
  event.target.value = ''
}

// 粘贴添加文件/图片：截图或复制文件后 Ctrl+V 直接加入待发送列表（纯文本粘贴不受影响）
const handlePaste = (event) => {
  const items = (event.clipboardData && event.clipboardData.items) || []
  const pastedFiles = []
  for (const item of items) {
    if (item.kind === 'file') {
      const file = item.getAsFile()
      if (file) pastedFiles.push(file)
    }
  }
  if (pastedFiles.length === 0) return
  // 剪贴板含文件时阻止默认行为（避免图片以base64文本形式落入输入框），统一走文件上传
  event.preventDefault()
  pastedFiles.forEach((file, i) => {
    const fallbackName = file.type.startsWith('image/')
      ? `粘贴图片_${Date.now()}_${i + 1}.png`
      : `粘贴文件_${Date.now()}_${i + 1}`
    selectedFiles.value.push({
      file: file,
      name: file.name || fallbackName,
      type: getFileType(file)
    })
  })
  ElMessage.success(`已粘贴添加 ${pastedFiles.length} 个文件，点击"发送"开始识别`)
}

// 移除文件
const removeFile = (index) => {
  selectedFiles.value.splice(index, 1)
}

// 发送消息
const sendMessage = async () => {
  if (isLoading.value) return

  const text = inputText.value.trim()
  if (!text && selectedFiles.value.length === 0) {
    alert('请输入文字或上传文件')
    return
  }

  // 添加用户消息
  const userMessage = {
    type: 'user',
    text: text || '上传了文件',
    files: selectedFiles.value.map(f => ({
      name: f.name,
      type: f.type
    }))
  }
  messages.value.push(userMessage)
  scrollToBottom()

  // 清空输入
  inputText.value = ''
  const filesToSend = [...selectedFiles.value]
  selectedFiles.value = []

  // 开始识别
  isLoading.value = true

  try {
    // 构造表单数据
    const formData = new FormData()
    if (text) {
      formData.append('text', text)
    }
    formData.append('source', isPwkMode.value ? '批文库添加' : '聊天识别')
    if (isPwkMode.value && props.pwkId) {
      formData.append('pwkId', props.pwkId)
    }

    // 添加所有文件（统一使用files字段）
    filesToSend.forEach(f => {
      formData.append('files', f.file)
    })

    // 调用API - 增加超时时间到10分钟（批文库模式调 /pwk/recognize，管理员模式调 /pzwh/recognize）
    const url = isPwkMode.value ? '/pwk/recognize' : '/pzwh/recognize'
    const response = await api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000 // 10分钟超时
    })

    // 响应拦截器已解包一层，response 实际是 { code, message, data, status }
    const resultData = response?.data || {}
    const responseCode = response?.code

    // 处理错误情况（如未登录）
    if (responseCode !== 200) {
      const errorMsg = response?.message || '请求失败'
      ElMessage.error(errorMsg)
      const aiMessage = {
        type: 'ai',
        text: errorMsg,
        result: resultData
      }
      messages.value.push(aiMessage)
      scrollToBottom()
      return
    }
    
    const isSuccess = resultData.success !== false
    let resultText = ''
    if (isPwkMode.value) {
      // 批文库添加模式：展示加入/重复/未找到统计
      const addedCount = resultData.addedCount || 0
      const duplicateCount = resultData.duplicateCount || 0
      const notFoundCount = resultData.notFoundCount || 0
      if (isSuccess || addedCount > 0 || duplicateCount > 0) {
        resultText = `识别完成！已加入批文库 ${addedCount} 条，库中已存在 ${duplicateCount} 条，未匹配到 ${notFoundCount} 条`
      } else {
        resultText = resultData.errorMessage || '识别失败，未找到匹配的批文'
      }
    } else {
      const savedCount = resultData.savedCount || 0
      const updatedCount = resultData.updatedCount || 0
      const totalCount = savedCount + updatedCount
      if (isSuccess && totalCount > 0) {
        resultText = `识别完成！新增 ${savedCount} 条，更新 ${updatedCount} 条`
      } else if (isSuccess && totalCount === 0) {
        resultText = '识别完成，未发现新的批文号'
      } else {
        resultText = resultData.errorMessage || '识别失败，请重试'
      }
    }

    const aiMessage = {
      type: 'ai',
      text: resultText,
      result: resultData
    }
    messages.value.push(aiMessage)
    scrollToBottom()

  } catch (error) {
    console.error('识别失败:', error)
    // 未登录：跳转登录页，登录后自动返回继续
    if (isUnauthorized(error)) {
      ElMessage.warning('请先登录')
      requireLogin(router, route.path)
      return
    }
    const errorMessage = {
      type: 'ai',
      text: '识别失败：' + (error.response?.data?.message || error.message || '未知错误')
    }
    messages.value.push(errorMessage)
    scrollToBottom()
  } finally {
    isLoading.value = false
    // 保存聊天记录
    saveMessagesToStorage()
  }
}

// 监听消息变化，自动保存
watch(messages, () => {
  saveMessagesToStorage()
}, { deep: true })

// 暴露方法
defineExpose({
  openDialog,
  showDialog
})

// 初始化
onMounted(() => {
  userStore.initFromStorage()
  // 清理过期的聊天记录
  cleanupExpiredMessages()
  if (route.path === '/adminChat') {
    showDialog.value = true
    // 加载聊天记录
    loadMessagesFromStorage()
  }
  userStore.initFromStorage()
})
</script>

<style scoped>
.pzwh-chat-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  max-width: 90vw;
  height: 600px;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  z-index: 1000;
}

.chat-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9fafb;
  border-radius: 12px 12px 0 0;
}

.chat-header h3 {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0 8px;
}

.close-btn:hover {
  color: #111827;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f3f4f6;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.message.user {
  justify-content: flex-end;
}

.message.ai {
  justify-content: flex-start;
}

.message-content {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 12px;
  position: relative;
}

.message.user .message-content {
  background: #3b82f6;
  color: white;
}

.message.ai .message-content {
  background: white;
  color: #111827;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.user-label,
.ai-label {
  font-size: 12px;
  margin-bottom: 4px;
  opacity: 0.8;
}

.message-text {
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-line;
}

.message-files {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-size: 12px;
}

.file-icon {
  font-size: 14px;
}

.file-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-result {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.message.user .message-result {
  border-top-color: rgba(255, 255, 255, 0.3);
}

.message.ai .message-result {
  border-top-color: #e5e7eb;
}

.result-summary,
.pzwh-list,
.price-list,
.saved-count,
.deleted-count,
.not-found-count,
.error-message {
  margin-bottom: 8px;
}

.pzwh-list ul,
.price-list ul,
.not-found-count ul {
  margin: 4px 0 0 20px;
  padding: 0;
}

.pzwh-list li,
.price-list li {
  margin: 4px 0;
}

.error {
  color: #ef4444;
}

.error-message {
  color: #ef4444;
}

.loading-dots::after {
  content: '...';
  animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
  0%,
  20% {
    content: '';
  }
  40% {
    content: '.';
  }
  60% {
    content: '..';
  }
  80%,
  100% {
    content: '...';
  }
}

.chat-input-area {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  background: white;
  border-radius: 0 0 12px 12px;
}

.file-upload-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  align-items: center;
}

.upload-btn {
  padding: 8px 16px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.upload-btn:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.upload-tip {
  font-size: 12px;
  color: #6b7280;
}

.selected-files {
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 14px;
}

.file-size {
  font-size: 12px;
  color: #6b7280;
}

.remove-file {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;
}

.remove-file:hover {
  color: #dc2626;
}

.input-area {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

textarea {
  flex: 1;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: none;
  font-family: inherit;
}

textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.send-btn {
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.send-btn:hover:not(:disabled) {
  background: #2563eb;
}

.send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
